import isPropValid from '@emotion/is-prop-valid';
import styled from 'styled-components';

import { unitMapping, type Unit } from '#src/lib/style.ts';

type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type Justify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';

type Props = {
  gap?: Unit;
  center?: boolean;
  grow?: boolean; // used when using inside another flex element.
  shrink?: boolean; // pass false to keep the element at its own size in a tight parent.
  align?: Align;
  justify?: Justify;
};

const ALIGN_ITEMS: Record<Align, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
};

const JUSTIFY_CONTENT: Record<Justify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

const alignItems =
  (whenNotCentered: Align) =>
  ({ align, center }: Props) =>
    ALIGN_ITEMS[align ?? (center ? 'center' : whenNotCentered)];

const justifyContent =
  (whenNotCentered: Justify) =>
  ({ justify, center }: Props) =>
    JUSTIFY_CONTENT[justify ?? (center ? 'center' : whenNotCentered)];

const styleOnlyProps = {
  shouldForwardProp: (prop: string, target: unknown) =>
    prop !== 'gap' &&
    prop !== 'center' &&
    prop !== 'grow' &&
    prop !== 'shrink' &&
    prop !== 'align' &&
    prop !== 'justify' &&
    (typeof target === 'string' ? isPropValid(prop) : true),
};

export const Horizontal = styled.div.withConfig(styleOnlyProps)<Props>`
  display: flex;
  align-items: ${alignItems('center')};
  justify-content: ${justifyContent('start')};
  gap: ${({ gap = 1 }) => unitMapping[gap]};
  flex: ${({ grow }) => (grow ? '1' : 'initial')};
  ${({ shrink }) => shrink === false && 'flex-shrink: 0;'}
`;

export const SBHorizontal = styled.div.withConfig(styleOnlyProps)<
  Exclude<Props, 'center'>
>`
  display: flex;
  align-items: ${alignItems('center')};
  justify-content: ${({ justify }) => JUSTIFY_CONTENT[justify ?? 'between']};
  gap: ${({ gap = 1 }) => unitMapping[gap]};
  flex: ${({ grow }) => (grow ? '1' : 'initial')};
  ${({ shrink }) => shrink === false && 'flex-shrink: 0;'}
`;

export const Vertical = styled.div.withConfig(styleOnlyProps)<Props>`
  display: flex;
  flex-direction: column;
  align-items: ${alignItems('stretch')};
  justify-content: ${justifyContent('start')};
  gap: ${({ gap = 1 }) => unitMapping[gap]};
  flex: ${({ grow }) => (grow ? '1' : 'initial')};
  ${({ shrink }) => shrink === false && 'flex-shrink: 0;'}
`;
